import { Component, CUSTOM_ELEMENTS_SCHEMA, inject, signal } from '@angular/core';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { TranslateService } from '@ngx-translate/core';
import { MaterialSharedModule } from '@app/shared/modules/material-shared.module';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { BaseSharedModule } from '@app/shared/modules/base-shared.module';
import { SharedModule } from '@app/shared/modules/shared.module';
import { Language } from '@app/shared/models';
import { LanguageFacade } from '@app/store/language';
import { LanguageOption } from '@app/store/language/models/language-state.models';
import { LoggerService } from '@app/core/infrastructure';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-language-selector',
  templateUrl: './language-selector.component.html',
  styleUrls: ['./language-selector.component.scss'],
  standalone: true,
  imports: [
    BaseSharedModule,
    MaterialSharedModule,
    AngularSvgIconModule,
    SharedModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class LanguageSelectorComponent {
  private readonly logger = inject(LoggerService).scope('LanguageSelectorComponent');
  
  currentLanguage = signal(Language.EN);
  private translate = inject(TranslateService);
  languageOptions = signal<LanguageOption[]>([]);

  constructor(
    private bottomSheetRef: MatBottomSheetRef<LanguageSelectorComponent>,
    private languageFacade: LanguageFacade
  ) {
    // Get language options from facade (translation keys)
    this.updateLanguageOptions();
    
    // Update when translations change
    this.translate.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.updateLanguageOptions();
      });
    
    // Subscribe to current language
    this.languageFacade.activeLanguage$
      .pipe(untilDestroyed(this))
      .subscribe((language: Language) => {
        this.currentLanguage.set(language);
      });
  }

  private updateLanguageOptions(): void {
    // Get options from facade and translate labels
    const options = this.languageFacade.getLanguageOptions();
    this.languageOptions.set(options.map(opt => ({
      ...opt,
      label: this.translate.instant(opt.label)
    })));
  }

  selectLanguage(language: Language): void {
    this.logger.debug('Language selected:', language);
    this.languageFacade.setActiveLanguage(language);
    this.languageFacade.updateUserPreferredLanguage(language);
    this.bottomSheetRef.dismiss();
  }

  isSelected(language: Language): boolean {
    return this.currentLanguage() === language;
  }
}
